// src/InvoiceModal.jsx

import React, { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas";

/* =============================
   1) PRICE MAP (from Excel file)
   ============================= */

const PRICE_MAP = {
  L: {
    Origin: {
      "30x40": 45000,
      "40x60": 70000,
      "50x70": 85000,
      "60x80": 100000,
      "70x100": 125000,
      "80x120": 185000,
      "120x180": 295000,
      "160x240": 395000,
      "30x30": 40000,
      "40x40": 45000,
      "50x50": 60000,
      "60x60": 65000,
      "70x70": 95000,
      "80x80": 100000,
      "90x90": 115000,
      "100x100": 135000,
      "110x110": 175000,
      "120x120": 185000,
      "130x130": 215000,
    },
    Edition: {
      "30x40": 75000,
      "40x60": 115000,
      "50x70": 135000,
      "60x80": 165000,
      "70x100": 195000,
      "80x120": 275000,
      "120x180": 395000,
      "160x240": 515000,
      "30x30": 75000,
      "40x40": 85000,
      "50x50": 100000,
      "60x60": 115000,
      "70x70": 150000,
      "80x80": 165000,
      "90x90": 185000,
      "100x100": 215000,
      "110x110": 265000,
      "120x120": 275000,
      "130x130": 315000,
    },
  },
  Kas: {
    Origin: {
      "30x40": 65000,
      "40x60": 75000,
      "50x70": 105000,
      "60x80": 140000,
      "70x100": 175000,
      "80x120": 245000,
      "120x180": 385000,
      "160x240": 495000,
    },
    Edition: {
      "30x40": 95000,
      "40x60": 115000,
      "50x70": 165000,
      "60x80": 205000,
      "70x100": 245000,
      "80x120": 325000,
      "120x180": 495000,
      "160x240": 615000,
    },
  },
};

const PANEL_TYPES = ["أوريجن", "إدشن", "برايم"];

// ✅ التسمية الظاهرة للزبون بالفاتورة فقط (لا تغيّر منطق التسعير أو القيمة المخزنة)
const PANEL_TYPE_INVOICE_LABELS = {
  "أوريجن": "طباعة فقط",
  "إدشن": "طباعة -رسم بنسبة من 35% إلى 50%",
  "برايم": "طباعة - رسم بنسبة من 85% إلى 95%",
};
const getPanelTypeInvoiceLabel = (panelType) =>
  PANEL_TYPE_INVOICE_LABELS[panelType] || panelType;

const FRAME_OPTIONS = ["بدون إطار", "إطار داخلي فقط", "إطار كامل"];

const FRAME_TYPES = [
  { value: "L", label: "L Frame" },
  { value: "Kas", label: "Kas Frame" },
];

const SIZE_OPTIONS = [
  "30x30", "30x40", "40x40", "40x60", "50x50", "50x70",
  "60x60", "60x80", "70x70", "70x100", "80x80", "80x120",
  "90x90", "100x100", "110x110", "120x120", "120x180", 
  "130x130", "160x240",
];

const toPriceTypeKey = (panelType) => {
  if (panelType === "أوريجن") return "Origin";
  if (panelType === "إدشن") return "Edition";
  return null;
};

const calculateItemPrice = (item) => {
  let pricePerPanel = item.pricePerPanel || 0;

  const canAutoPrice =
    item.sizeKey &&
    (item.panelType === "أوريجن" || item.panelType === "إدشن") &&
    item.frameType &&
    item.sizeSource === "list";

  if (canAutoPrice) {
    const typeKey = toPriceTypeKey(item.panelType);
    const framePrices =
      PRICE_MAP[item.frameType] &&
      PRICE_MAP[item.frameType][typeKey || "Origin"];

    if (framePrices && framePrices[item.sizeKey]) {
      pricePerPanel = framePrices[item.sizeKey];
    }
  }

  const quantity = Number(item.quantity || 0);
  const total = pricePerPanel * quantity;

  return {
    ...item,
    pricePerPanel,
    total,
  };
};

/* =============================
   2) Default Invoice Templates
   ============================= */

const defaultInvoice = {
  customerName: "",
  projectName: "",
  invoiceNumber: "20250001",
  governorate: "",
  nearestPoint: "",
  date: new Date().toISOString().substring(0, 10),
  items: [],
  subtotal: 0,
  consultationFee: 0,
  transportFee: 0,
  discount: 0,
  finalTotal: 0,
};

const emptyItem = {
  panelName: "",
  panelType: "",
  frameOption: "",
  frameType: "",
  sizeSource: "list",
  sizeKey: "",
  quantity: 1,
  pricePerPanel: 0,
  total: 0,
};

/* =============================
   3) MAIN COMPONENT
   ============================= */

const InvoiceModal = ({ isOpen, onClose, onSave, existingData }) => {
  const [invoice, setInvoice] = useState(defaultInvoice);
  const [currentItem, setCurrentItem] = useState(emptyItem);
  const [showItemForm, setShowItemForm] = useState(false);

  const previewRef = useRef(null);

  useEffect(() => {
    if (existingData) {
      setInvoice((prev) => ({
        ...prev,
        ...existingData,
        items: existingData.items || [],
      }));
    } else {
      setInvoice(defaultInvoice);
    }
  }, [existingData, isOpen]);

  if (!isOpen) return null;

  /* =============================
     4) HEADER CHANGE (RIGHT SIDE)
     ============================= */
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setInvoice((prev) => {
      const updated = {
        ...prev,
        [name]:
          name === "consultationFee" ||
          name === "transportFee" ||
          name === "discount"
            ? Number(value || 0)
            : value,
      };

      const subtotal = updated.subtotal || 0;
      updated.finalTotal =
        subtotal +
        (updated.consultationFee || 0) +
        (updated.transportFee || 0) -
        (updated.discount || 0);

      return updated;
    });
  };

  /* =============================
     5) ITEM FORM CHANGE
     ============================= */

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem((prev) => {
      let updated = { ...prev };

      updated[name] = name === "quantity" || name === "pricePerPanel"
        ? Number(value || 0)
        : value;
if (name === "frameOption" && value !== "إطار كامل") {
  updated.frameType = "";
}

      if (name === "sizeKey") {
        updated.sizeSource = value === "custom" ? "custom" : "list";
      }
if (name === "sizeKey" && value === "custom") {
  updated.sizeSource = "custom";
  updated.sizeKey = "";
  updated.customHeight = "";
  updated.customWidth = "";
  updated.pricePerPanel = 0; // السعر يدوي فقط
}

      updated = calculateItemPrice(updated);
      return updated;
    });
  };

  /* =============================
     6) ADD ITEM
     ============================= */

  const addItem = () => {
    if (!currentItem.panelName || !currentItem.panelType || !currentItem.sizeKey) {
      alert("يرجى إدخال اسم اللوحة + نوع اللوحة + القياس");
      return;
    }

    const updatedItems = [...invoice.items, currentItem];
    const subtotal = updatedItems.reduce((sum, it) => sum + it.total, 0);

    setInvoice((prev) => ({
      ...prev,
      items: updatedItems,
      subtotal,
      finalTotal:
        subtotal +
        (prev.consultationFee || 0) +
        (prev.transportFee || 0) -
        (prev.discount || 0),
    }));

    setCurrentItem(emptyItem);
    setShowItemForm(false);
  };

  /* =============================
     7) REMOVE ITEM
     ============================= */

  const removeItem = (index) => {
    const newItems = invoice.items.filter((_, i) => i !== index);
    const subtotal = newItems.reduce((sum, it) => sum + it.total, 0);

    setInvoice((prev) => ({
      ...prev,
      items: newItems,
      subtotal,
      finalTotal:
        subtotal +
        (prev.consultationFee || 0) +
        (prev.transportFee || 0) -
        (prev.discount || 0),
    }));
  };

  /* =============================
     8) SAVE + EXPORT
     ============================= */

  const handleSaveInvoice = () => {
    onSave(invoice);
    onClose();
  };

  const handleExportImage = async () => {
    if (!previewRef.current) return;

    const canvas = await html2canvas(previewRef.current, {
      scale: 2,
      useCORS: true,
    });

    const dataURL = canvas.toDataURL("image/png");

    const link = document.createElement("a");
    link.href = dataURL;
    link.download = `invoice-${invoice.invoiceNumber}.png`;
    link.click();
  };

  const isAutoPrice =
    currentItem.sizeSource === "list" &&
    (currentItem.panelType === "أوريجن" || currentItem.panelType === "إدشن") &&
    !!currentItem.frameType;

  /* =============================
       UI START HERE
     ============================= */

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.45)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          backgroundColor: "#f4f4f4",
          borderRadius: 12,
          width: "1200px",
          height: "92vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* HEADER BAR */}
        <div
          style={{
            padding: "12px 20px",
            background: "#fff",
            borderBottom: "1px solid #ddd",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <h3 style={{ margin: 0 }}>🧾 إنشاء فاتورة أرتيكا</h3>

          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 22,
              cursor: "pointer",
            }}
          >
            ✖
          </button>
        </div>

        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

          {/* ======================================================
                RIGHT SIDE — FORM UI (NEW DESIGN)
              ====================================================== */}
          <div
            style={{
              width: "38%",
              padding: 20,
              borderRight: "1px solid #ddd",
              overflowY: "auto",
              background: "#fdfdfd",
              fontFamily: "sans-serif",
            }}
          >

            {/* CARD: Customer Info */}
            <div
              style={{
                background: "#fff",
                padding: "18px 22px",
                borderRadius: "10px",
                boxShadow: "0 0 8px rgba(0,0,0,0.08)",
                border: "1px solid #eee",
                marginBottom: 20,
              }}
            >
              <h3
                style={{
                  textAlign: "right",
                  marginBottom: 18,
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                معلومات الزبون
              </h3>

              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                
                {/* NAME */}
                <div>
                  <label style={{ fontSize: 13, color: "#555" }}>اسم الزبون</label>
                  <input
                    type="text"
                    name="customerName"
                    style={inputStyle}
                    value={invoice.customerName}
                    onChange={handleHeaderChange}
                  />
                </div>

                {/* PROJECT NAME */}
                <div>
                  <label style={{ fontSize: 13, color: "#555" }}>
                    اسم المشروع (اختياري)
                  </label>
                  <input
                    type="text"
                    name="projectName"
                    style={inputStyle}
                    value={invoice.projectName}
                    onChange={handleHeaderChange}
                  />
                </div>

                {/* INVOICE NUMBER */}
                <div>
                  <label style={{ fontSize: 13, color: "#555" }}>رقم الفاتورة</label>
                  <input
                    type="text"
                    name="invoiceNumber"
                    style={inputStyle}
                    value={invoice.invoiceNumber}
                    onChange={handleHeaderChange}
                  />
                </div>

                {/* GOVERNORATE */}
                <div>
                  <label style={{ fontSize: 13, color: "#555" }}>المحافظة</label>
                  <select
                    name="governorate"
                    style={{ ...inputStyle, background: "#fff" }}
                    value={invoice.governorate}
                    onChange={handleHeaderChange}
                  >
                    <option value="">اختر المحافظة</option>
<option value="بغداد">بغداد</option>
<option value="البصرة">البصرة</option>
<option value="نينوى">نينوى</option>
<option value="أربيل">أربيل</option>
<option value="السليمانية">السليمانية</option>
<option value="دهوك">دهوك</option>
<option value="الأنبار">الأنبار</option>ِ
<option value="كربلاء">كربلاء</option>
<option value="النجف">النجف</option>
<option value="بابل">بابل</option>
<option value="واسط">واسط</option>
<option value="ميسان">ميسان</option>
<option value="ذي قار">ذي قار</option>
<option value="الديوانية">الديوانية (القادسية)</option>
<option value="صلاح الدين">صلاح الدين</option>
<option value="ديالى">ديالى</option>
<option value="كركوك">كركوك</option>
<option value="المثنى">المثنى</option>
<option value="حلبجة">حلبچة</option>

                  </select>
                </div>

                {/* LOCATION */}
                <div>
                  <label style={{ fontSize: 13, color: "#555" }}>
                    أقرب نقطة دالة
                  </label>
                  <input
                    type="text"
                    name="nearestPoint"
                    style={inputStyle}
                    value={invoice.nearestPoint}
                    onChange={handleHeaderChange}
                  />
                </div>

                {/* DATE */}
                <div>
                  <label style={{ fontSize: 13, color: "#555" }}>التاريخ</label>
                  <input
                    type="date"
                    name="date"
                    style={inputStyle}
                    value={invoice.date}
                    onChange={handleHeaderChange}
                  />
                </div>
              </div>
            </div>

            {/* ======================================================
                  CARD: ADD ITEM
               ====================================================== */}

            <div
              style={{
                background: "#fff",
                padding: "18px 22px",
                borderRadius: "10px",
                boxShadow: "0 0 8px rgba(0,0,0,0.08)",
                border: "1px solid #eee",
                marginBottom: 20,
              }}
            >
              <h3
                style={{
                  textAlign: "right",
                  marginBottom: 18,
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                إضافة لوحة
              </h3>

              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => setShowItemForm(!showItemForm)}
                style={{
                  marginBottom: 14,
                  background: "white",
                  border: "1px solid #777",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                {showItemForm ? "إخفاء الفورم" : "➕ لوحة جديدة"}
              </button>

              {showItemForm && (
                <div
                  style={{
                    marginTop: 8,
                    padding: 14,
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    background: "#fafafa",
                  }}
                >
                  {/* NAME */}
                  <div style={fieldBlock}>
                    <label style={labelStyle}>اسم اللوحة</label>
                    <input
                      type="text"
                      name="panelName"
                      style={inputStyle}
                      value={currentItem.panelName}
                      onChange={handleItemChange}
                    />
                  </div>

                  {/* TYPE */}
                  <div style={fieldBlock}>
                    <label style={labelStyle}>نوع اللوحة</label>
                    <select
                      name="panelType"
                      style={{ ...inputStyle, background: "#fff" }}
                      value={currentItem.panelType}
                      onChange={handleItemChange}
                    >
                      <option value="">اختر النوع</option>
                      {PANEL_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  {/* FRAME OPTION */}
                  <div style={fieldBlock}>
                    <label style={labelStyle}>الإطار؟</label>
                    <select
                      name="frameOption"
                      style={{ ...inputStyle, background: "#fff" }}
                      value={currentItem.frameOption}
                      onChange={handleItemChange}
                    >
                      <option value="">اختر خيار الإطار</option>
                      {FRAME_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>

               {/* FRAME TYPE — يظهر فقط عند اختيار "إطار كامل" */}
{currentItem.frameOption === "إطار كامل" && (
  <div style={fieldBlock}>
    <label style={labelStyle}>نوع الإطار</label>
    <select
      name="frameType"
      style={{ ...inputStyle, background: "#fff" }}
      value={currentItem.frameType}
      onChange={handleItemChange}
    >
      <option value="">اختر نوع الإطار</option>
      {FRAME_TYPES.map((f) => (
        <option key={f.value} value={f.value}>{f.label}</option>
      ))}
    </select>
  </div>
)}

                  {/* SIZE */}
                  <div style={fieldBlock}>
                    <label style={labelStyle}>القياس</label>
                    <select
                      name="sizeKey"
                      style={{ ...inputStyle, background: "#fff" }}
                      value={
                        currentItem.sizeSource === "list"
                          ? currentItem.sizeKey
                          : "custom"
                      }
                      onChange={handleItemChange}
                    >
                      <option value="">اختر القياس</option>
                      {SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size.replace("x", "×")}
                        </option>
                      ))}
                      <option value="custom">أخرى (يدوي)</option>
                    </select>
                    {/* قياس يدوي يظهر فقط عند اختيار "أخرى" */}
{currentItem.sizeSource === "custom" && (
  <div
    style={{
      display: "flex",
      gap: "10px",
      marginTop: "10px",
      marginBottom: "10px",
    }}
  >
    <div style={{ flex: 1 }}>
      <label style={labelStyle}>الطول (سم)</label>
      <input
        type="number"
        name="customHeight"
        style={inputStyle}
        min="1"
        onChange={(e) =>
          setCurrentItem((prev) => {
            const height = e.target.value;
            const width = prev.customWidth || "";
            return {
              ...prev,
              customHeight: height,
              sizeKey: width && height ? `${width}x${height}` : "",
            };
          })
        }
      />
    </div>

    <div style={{ flex: 1 }}>
      <label style={labelStyle}>العرض (سم)</label>
      <input
        type="number"
        name="customWidth"
        style={inputStyle}
        min="1"
        onChange={(e) =>
          setCurrentItem((prev) => {
            const width = e.target.value;
            const height = prev.customHeight || "";
            return {
              ...prev,
              customWidth: width,
              sizeKey: width && height ? `${width}x${height}` : "",
            };
          })
        }
      />
    </div>
  </div>
)}

                  </div>

                  {/* QUANTITY */}
                  <div style={fieldBlock}>
                    <label style={labelStyle}>العدد</label>
                    <input
                      type="number"
                      name="quantity"
                      style={inputStyle}
                      min={1}
                      value={currentItem.quantity}
                      onChange={handleItemChange}
                    />
                  </div>

                  {/* PRICE */}
                  <div style={fieldBlock}>
                    <label style={labelStyle}>سعر اللوحة (دينار)</label>
                    <input
                      type="number"
                      name="pricePerPanel"
                      style={inputStyle}
                      value={currentItem.pricePerPanel}
                      onChange={handleItemChange}
                      readOnly={isAutoPrice}
                    />
                    {isAutoPrice && (
                      <small style={{ color: "#777" }}>
                        السعر محسوب تلقائيًا حسب الجدول.
                      </small>
                    )}
                  </div>

                  {/* TOTAL */}
                  <div style={fieldBlock}>
                    <label style={labelStyle}>المجموع لهذا السطر</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={currentItem.total}
                      readOnly
                    />
                  </div>

                  {/* BUTTONS */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 10,
                      marginTop: 10,
                    }}
                  >
                    <button
                      className="btn btn-secondary"
                      style={cancelBtn}
                      onClick={() => {
                        setShowItemForm(false);
                        setCurrentItem(emptyItem);
                      }}
                    >
                      إلغاء
                    </button>

                    <button
                      className="btn btn-primary"
                      style={saveBtn}
                      onClick={addItem}
                    >
                      حفظ اللوحة
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ======================================================
                  CARD: TOTALS
               ====================================================== */}

            <div
              style={{
                background: "#fff",
                padding: "18px 22px",
                borderRadius: "10px",
                boxShadow: "0 0 8px rgba(0,0,0,0.08)",
                border: "1px solid #eee",
                marginBottom: 20,
              }}
            >
              <h3
                style={{
                  textAlign: "right",
                  marginBottom: 18,
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                المجاميع
              </h3>

              <div style={fieldBlock}>
                <label style={labelStyle}>المجموع الأولي (من اللوحات)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={invoice.subtotal}
                  readOnly
                />
              </div>

              <div style={fieldBlock}>
                <label style={labelStyle}>أجور الاستشارة</label>
                <input
                  type="number"
                  name="consultationFee"
                  style={inputStyle}
                  value={invoice.consultationFee}
                  onChange={handleHeaderChange}
                />
              </div>

              <div style={fieldBlock}>
                <label style={labelStyle}>أجور النقل</label>
                <input
                  type="number"
                  name="transportFee"
                  style={inputStyle}
                  value={invoice.transportFee}
                  onChange={handleHeaderChange}
                />
              </div>

              <div style={fieldBlock}>
                <label style={labelStyle}>الخصم</label>
                <input
                  type="number"
                  name="discount"
                  style={inputStyle}
                  value={invoice.discount}
                  onChange={handleHeaderChange}
                />
              </div>

              <div style={fieldBlock}>
                <label style={labelStyle}>المجموع النهائي</label>
                <input
                  type="number"
                  style={inputStyle}
value={invoice.finalTotal.toLocaleString()}
                  readOnly
                />
              </div>

              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                }}
              >
                <button
                  type="button"
                  style={exportBtn}
                  onClick={handleExportImage}
                >
                  💾 حفظ الفاتورة كصورة
                </button>

                <button
                  type="button"
                  style={saveBtn}
                  onClick={handleSaveInvoice}
                >
                  حفظ الفاتورة والرجوع
                </button>
              </div>
            </div>
          </div>

          {/* ======================================================
                LEFT SIDE — ORIGINAL INVOICE PREVIEW (THIME B)
              ====================================================== */}

          <div
            style={{
              flex: 1,
              padding: 16,
              overflow: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              backgroundColor: "#f4f4f4",
            }}
          >
            <div
              ref={previewRef}
              style={{
                width: "840px",
                minHeight: "600px",
                backgroundColor: "#ffffff",
                boxShadow: "0 0 8px rgba(0,0,0,0.15)",
                padding: "40px 40px 30px 40px",
                direction: "rtl",
                position: "relative",
              }}
            >
              {/* LOGO + TITLE */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 30,
                }}
              >
                <div>
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      border: "2px solid #000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: 18,
                    }}
                  >
                    A
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <h2 style={{ margin: 0 }}>أرتيكا</h2>
                </div>
              </div>

              {/* TABLE: DATE + INVOICE NUMBER */}
              <div
                style={{
                  position: "absolute",
                  top: 80,
                  left: 40,
                  border: "1px solid #000",
                  fontSize: 12,
                }}
              >
                <div style={tableLeftTitleRow}>
                  <div style={tableLeftTitle}>التاريخ</div>
                  <div style={tableLeftValue}>{invoice.date}</div>
                </div>

                <div style={tableLeftTitleRow}>
                  <div style={tableLeftTitle}>رقم الفاتورة</div>
                  <div style={tableLeftValue}>{invoice.invoiceNumber}</div>
                </div>

                <div style={tableLeftTitleRow}>
                  <div style={tableLeftTitle}>صالحة لغاية</div>
                  <div style={tableLeftValue}>{invoice.date}</div>
                </div>
              </div>

              {/* CUSTOMER INFO (black header) */}
              <div
                style={{
                  textAlign: "right",
                  marginBottom: 25,
                  marginTop: 20,
                }}
              >
                <div style={customerHeader}>معلومات الزبون</div>

                <div style={customerDetails}>
                  <div>اسم الزبون: {invoice.customerName || "-"}</div>
                  {invoice.projectName && (
                    <div>المشروع: {invoice.projectName}</div>
                  )}
                  <div>
                    العنوان: {invoice.governorate || "-"}{" "}
                    {invoice.nearestPoint && `، ${invoice.nearestPoint}`}
                  </div>
                </div>
              </div>

              {/* TABLE: ITEMS */}
              <table style={invoiceTable}>
                <thead>
                  <tr>
                    <th style={invoiceTh}>السعر الكلي</th>
                    <th style={invoiceTh}>سعر اللوحة</th>
                    <th style={invoiceTh}>العدد</th>
                    <th style={invoiceTh}>الأبعاد</th>
                    <th style={invoiceTh}>الإطار</th>
                    <th style={invoiceTh}>الوصف</th>
                  </tr>
                </thead>

                <tbody>
                  {invoice.items.length === 0 && (
                    <tr>
                      <td colSpan={6} style={invoiceTdCenter}>
                        لا توجد لوحات بعد
                      </td>
                    </tr>
                  )}

                  {invoice.items.map((item, i) => (
                    <tr key={i}>
                      <td style={invoiceTd}>
                        {item.total.toLocaleString()}
                      </td>
                      <td style={invoiceTd}>
                        {item.pricePerPanel.toLocaleString()}
                      </td>
                      <td style={invoiceTd}>{item.quantity}</td>
                      <td style={invoiceTd}>
                        {item.sizeKey.replace("x", "×")}
                      </td>
                      <td style={invoiceTd}>{item.frameOption}</td>
                      <td
  style={{
    ...invoiceTd,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    direction: "rtl",
    unicodeBidi: "bidi-override",
  }}
>
  {item.panelName} – {getPanelTypeInvoiceLabel(item.panelType)}
</td>

                    </tr>
                  ))}
                </tbody>
              </table>

              {/* SUBTOTAL BLOCK */}
              <table style={subtotalTable}>
                <tbody>
                  <tr>
                    <td style={subtotalTitle}>المجموع الأولي</td>
                    <td style={subtotalValue}>
                      {invoice.subtotal.toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* =============================
   REUSABLE STYLES
   ============================= */

const inputStyle = {
  width: "100%",
  padding: "9px 10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: 13,
  marginTop: 4,
};

const fieldBlock = { marginBottom: 12 };

const labelStyle = { fontSize: 13, color: "#555" };

const cancelBtn = {
  padding: "6px 14px",
  borderRadius: "6px",
  background: "#ccc",
  border: "none",
  cursor: "pointer",
};

const saveBtn = {
  padding: "6px 14px",
  borderRadius: "6px",
  background: "#007bff",
  color: "#fff",
  border: "none",
  cursor: "pointer",
};

const exportBtn = {
  padding: "6px 14px",
  borderRadius: "6px",
  background: "#6c757d",
  color: "#fff",
  border: "none",
  cursor: "pointer",
};

const tableLeftTitleRow = {
  display: "flex",
  borderBottom: "1px solid #000",
};

const tableLeftTitle = {
  padding: "4px 8px",
  borderLeft: "1px solid #000",
  backgroundColor: "#f5f5f5",
  minWidth: 80,
};

const tableLeftValue = {
  padding: "4px 8px",
  minWidth: 90,
};

const customerHeader = {
  backgroundColor: "#000",
  color: "#fff",
  padding: "8px 14px",
  display: "inline-block",
  fontSize: 12,
};

const customerDetails = {
  fontSize: 12,
  marginTop: 6,
  lineHeight: 1.4,
};

const invoiceTable = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 12,
  marginTop: 40,
};

const invoiceTh = {
  border: "1px solid #000",
  padding: "6px 4px",
  backgroundColor: "#000",
  color: "#fff",
};

const invoiceTd = {
  border: "1px solid #000",
  padding: "6px 4px",
  textAlign: "center",
};

const invoiceTdCenter = {
  border: "1px solid #000",
  padding: 8,
  textAlign: "center",
  color: "#555",
};

const subtotalTable = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: 0,
  fontSize: 12,
};

const subtotalTitle = {
  border: "1px solid #000",
  padding: "6px 4px",
  backgroundColor: "#000",
  color: "#fff",
  textAlign: "right",
};

const subtotalValue = {
  border: "1px solid #000",
  padding: "6px 4px",
  textAlign: "center",
};

export default InvoiceModal;
